import { z } from 'zod';
import {
  DEFAULT_PROJECT_TIMEZONE,
  DEFAULT_RISK_HORIZON_DAYS,
} from '@obraplan/core/domain/calendar/constants';
import { MAX_FLOOR_RANGE } from '@obraplan/core/domain/structure/floor';
import { businessDateSchema, idSchema, nonEmptyString, optionalTrimmed } from './common';
import { measurementModeSchema } from './team-measurement';

/**
 * A leitura leve da estrutura (Fase 26): torres, pavimentos e frentes, sem os itens. É o
 * que as caixas de filtro precisam — a árvore inteira de uma obra com o orçamento importado
 * passa de 10 MB.
 */
export const projectOutlineSchema = z.object({ projectId: idSchema });
export type ProjectOutlineInput = z.infer<typeof projectOutlineSchema>;

// ── Obra ────────────────────────────────────────────────────────────────────────

export const createProjectSchema = z.object({
  code: nonEmptyString('Código', 30),
  name: nonEmptyString('Nome', 160),
  client: optionalTrimmed(160),
  address: optionalTrimmed(300),
  /** Quem assina a obra tecnicamente — sai no cabeçalho do RDO (Fase 18). */
  technicalManager: optionalTrimmed(160),
  timezone: z.string().min(1).default(DEFAULT_PROJECT_TIMEZONE),
  startDate: businessDateSchema.optional(),
  endDate: businessDateSchema.optional(),
  /** O id desta obra no sistema de compras do cliente. Ausente = obra não requisita. */
  externalId: optionalTrimmed(64),
  /** Como a Conferência mede (RN-405). Ausente = por pessoa, o padrão. */
  measurementMode: measurementModeSchema.optional(),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  projectId: idSchema,
  name: nonEmptyString('Nome', 160),
  client: optionalTrimmed(160),
  address: optionalTrimmed(300),
  technicalManager: optionalTrimmed(160),
  status: z.enum(['PLANNING', 'ACTIVE', 'PAUSED', 'FINISHED', 'ARCHIVED']),
  timezone: z.string().min(1),
  riskHorizonDays: z.number().int().min(0).max(90).default(DEFAULT_RISK_HORIZON_DAYS),
  requireDistinctInspector: z.boolean().default(false),
  /**
   * Como a Conferência mede (RN-405, Fase 30). **Ausente mantém** o modo que a obra tem: a tela
   * que não conhece o campo não pode devolver a obra por equipe para a medição por pessoa.
   */
  measurementMode: measurementModeSchema.optional(),
  startDate: businessDateSchema.optional(),
  endDate: businessDateSchema.optional(),
  /**
   * O destino no sistema de compras — e o único campo em que **ausente e vazio são
   * coisas diferentes**.
   *
   * `optionalTrimmed` transformaria `''` em `undefined`, e os dois casos passariam a
   * significar a mesma coisa. Aqui eles não podem: ausente **mantém** o vínculo (protege
   * a tela que não tem o campo de apagá-lo em silêncio) e vazio **desliga** (é a pessoa
   * escolhendo "não requisita material"). Sem a distinção, essa opção não teria efeito
   * nenhum — e uma opção que não faz nada é pior do que uma opção ausente.
   */
  externalId: z.string().trim().max(64).optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

// ── Versões de orçamento da obra (07/10/2026) ───────────────────────────────────

/**
 * "Duplicar obra": a versão nova leva o projeto e o orçamento da obra informada — que pode ser
 * a aprovada ou outra versão — e nasce ligada à aprovada da família (`versionOf`), com o
 * próximo número. `name` ausente é `versionName(aprovada, número)`. Devolve a obra nova.
 */
export const duplicateProjectSchema = z.object({
  projectId: idSchema,
  name: optionalTrimmed(160),
});
export type DuplicateProjectInput = z.infer<typeof duplicateProjectSchema>;

/** Exclui uma versão e tudo o que foi copiado para ela. A obra aprovada não se exclui aqui. */
export const deleteProjectVersionSchema = z.object({ projectId: idSchema });
export type DeleteProjectVersionInput = z.infer<typeof deleteProjectVersionSchema>;

/**
 * As escolhas da aprovação para o que a operação não acha sozinha na versão (Fase 36, RN-467,
 * RN-468): por alvo sem par, o alvo da versão ou `KEEP` (fica na antiga); por conflito de
 * mesmo lugar, o id da linha que vale. As chaves são as que a prévia devolve.
 */
const escolhasSchema = z.record(z.string().min(1).max(200), z.string().min(1).max(200));
export const transferChoicesSchema = z.object({
  missing: escolhasSchema.optional(),
  collisions: escolhasSchema.optional(),
});

/**
 * "Aprovar versão": esta versão vira a obra que a execução acompanha (sai o `versionOf`), a
 * aprovada de antes vira versão (ganha `versionOf` e o próximo número) e as outras versões da
 * família passam a apontar para a nova aprovada. Toda a operação da aprovada de antes vem junto
 * (Fase 36, RN-465), cada linha para o par do que ela apontava; o que não acha lugar, ou cai no
 * mesmo lugar de outra, espera as `choices` (a prévia diz quais). Devolve a obra aprovada.
 */
export const approveProjectVersionSchema = z.object({
  projectId: idSchema,
  choices: transferChoicesSchema.optional(),
});
export type ApproveProjectVersionInput = z.infer<typeof approveProjectVersionSchema>;

/**
 * "Levar para a obra aprovada" (Fase 38, RN-487): o quantitativo dos serviços escolhidos vai da
 * versão para a obra que a execução acompanha, e fica aberto lá. Os serviços vêm pela chave da
 * trava (`serviceLockKey`), a mesma nas duas obras.
 */
export const transferServicesToApprovedSchema = z.object({
  projectId: idSchema,
  serviceKeys: z.array(z.string().trim().min(1).max(400)).min(1).max(500),
});
export type TransferServicesToApprovedInput = z.infer<typeof transferServicesToApprovedSchema>;

export interface ServicesTransferredToApproved {
  readonly approved: { readonly id: string; readonly name: string };
  /** Os serviços que foram, na ordem do pedido. */
  readonly services: readonly {
    readonly key: string;
    readonly disciplineName: string;
    readonly serviceName: string;
  }[];
  /** Quantas linhas foram, por tabela. */
  readonly summary: Readonly<Record<string, number>>;
}

/** A prévia da aprovação (Fase 36): o que vai, e o que espera escolha. */
export const previewProjectVersionApprovalSchema = approveProjectVersionSchema;

export interface ProjectVersionApprovalPreview {
  readonly approved: { readonly id: string; readonly name: string };
  readonly version: { readonly id: string; readonly name: string };
  /** O que vai, por tabela, com o nome que a pessoa reconhece. */
  readonly moves: readonly {
    readonly table: string;
    readonly label: string;
    readonly count: number;
  }[];
  /** O que fica na antiga pelas escolhas feitas. */
  readonly keeps: readonly {
    readonly table: string;
    readonly label: string;
    readonly count: number;
  }[];
  readonly missing: readonly MissingTargetView[];
  readonly collisions: readonly TransferCollisionView[];
  /** Nada espera escolha: aprovar segue direto. */
  readonly ready: boolean;
}

export interface MissingTargetView {
  readonly key: string;
  readonly kind: string;
  readonly kindLabel: string;
  readonly label: string;
  readonly rows: readonly {
    readonly table: string;
    readonly label: string;
    readonly count: number;
  }[];
  readonly canKeep: boolean;
  readonly options: readonly { readonly id: string; readonly label: string }[];
}

export interface TransferCollisionView {
  readonly key: string;
  readonly table: string;
  readonly tableLabel: string;
  readonly candidates: readonly {
    readonly id: string;
    readonly label: string;
    readonly from: 'APPROVED' | 'VERSION';
    readonly ifLoses: 'KEEP' | 'DISCARD' | 'CANNOT_LOSE';
  }[];
}

// ── Torre ───────────────────────────────────────────────────────────────────────

export const createTowerSchema = z.object({
  projectId: idSchema,
  name: nonEmptyString('Nome da torre', 120),
  code: optionalTrimmed(30),
});
export type CreateTowerInput = z.infer<typeof createTowerSchema>;

export const renameTowerSchema = z.object({
  towerId: idSchema,
  name: nonEmptyString('Nome da torre', 120),
  code: optionalTrimmed(30),
});

// ── Pavimento ───────────────────────────────────────────────────────────────────

export const createFloorSchema = z.object({
  towerId: idSchema,
  name: nonEmptyString('Nome do pavimento', 120),
  code: optionalTrimmed(30),
  level: z.number().int().min(-20).max(300),
});
export type CreateFloorInput = z.infer<typeof createFloorSchema>;

export const createFloorRangeSchema = z
  .object({
    towerId: idSchema,
    fromLevel: z.number().int().min(-20).max(300),
    toLevel: z.number().int().min(-20).max(300),
    namePattern: nonEmptyString('Modelo do nome', 60),
    /** O modelo dos negativos; ausente = `{n}º Subsolo` (RN-239). */
    belowGroundPattern: nonEmptyString('Modelo do nome abaixo do térreo', 60).optional(),
  })
  .refine((value) => value.fromLevel <= value.toLevel, {
    message: 'O nível inicial deve ser menor ou igual ao final.',
    path: ['toLevel'],
  })
  .refine((value) => value.toLevel - value.fromLevel + 1 <= MAX_FLOOR_RANGE, {
    message: `Crie no máximo ${String(MAX_FLOOR_RANGE)} pavimentos de uma vez.`,
    path: ['toLevel'],
  });
export type CreateFloorRangeInput = z.infer<typeof createFloorRangeSchema>;

export const renameFloorSchema = z.object({
  floorId: idSchema,
  name: nonEmptyString('Nome do pavimento', 120),
  code: optionalTrimmed(30),
  level: z.number().int().min(-20).max(300),
});

// ── Disciplina ──────────────────────────────────────────────────────────────────

export const createDisciplineSchema = z.object({
  /** Ausente = disciplina global do sistema (RN-007). */
  projectId: idSchema.optional(),
  name: nonEmptyString('Nome da disciplina', 80),
  code: optionalTrimmed(30),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use uma cor no formato #RRGGBB.')
    .optional(),
  /** Unidade típica do serviço — a atividade herda ao nascer. */
  unitOfMeasureId: idSchema.optional(),
});
export type CreateDisciplineInput = z.infer<typeof createDisciplineSchema>;

/**
 * Edição de uma disciplina já cadastrada.
 *
 * O nome entra aqui porque a identidade da disciplina é ele — corrigir "Hidraulica" para
 * "Hidráulica" é edição, não uma disciplina nova. `null` nos opcionais limpa o campo.
 */
export const updateDisciplineSchema = z.object({
  disciplineId: idSchema,
  name: nonEmptyString('Nome da disciplina', 80),
  code: optionalTrimmed(30),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use uma cor no formato #RRGGBB.')
    .optional(),
  unitOfMeasureId: idSchema.optional(),
});
export type UpdateDisciplineInput = z.infer<typeof updateDisciplineSchema>;

/** Liga e desliga a disciplina no catálogo — desligar não apaga as frentes existentes. */
export const setDisciplineActiveSchema = z.object({
  disciplineId: idSchema,
  isActive: z.boolean(),
});
export type SetDisciplineActiveInput = z.infer<typeof setDisciplineActiveSchema>;

// ── Atividade ───────────────────────────────────────────────────────────────────

export const createActivitySchema = z.object({
  floorId: idSchema,
  disciplineId: idSchema,
  notes: optionalTrimmed(1000),
});
export type CreateActivityInput = z.infer<typeof createActivitySchema>;

/**
 * Troca a unidade de medição da frente.
 *
 * `null` remove — uma frente pode deixar de ser medida. As quantidades já lançadas
 * continuam como estão, na unidade em que foram escritas.
 */
/** `null` remove a medição da disciplina — e das frentes que a seguem. */
export const changeDisciplineUnitSchema = z.object({
  disciplineId: idSchema,
  unitOfMeasureId: idSchema.nullable(),
});
export type ChangeDisciplineUnitInput = z.infer<typeof changeDisciplineUnitSchema>;

export const changeActivityUnitSchema = z.object({
  activityId: idSchema,
  unitOfMeasureId: idSchema.nullable(),
});
export type ChangeActivityUnitInput = z.infer<typeof changeActivityUnitSchema>;

export const applyDisciplinesSchema = z.object({
  floorIds: z.array(idSchema).min(1, 'Escolha ao menos um pavimento.'),
  disciplineIds: z.array(idSchema).min(1, 'Escolha ao menos uma disciplina.'),
});
export type ApplyDisciplinesInput = z.infer<typeof applyDisciplinesSchema>;

export const changeActivityStatusSchema = z.object({
  activityId: idSchema,
  status: z.enum(['PLANNED', 'IN_PROGRESS', 'DONE', 'CANCELLED']),
});

export const updateActivityScheduleSchema = z.object({
  activityId: idSchema,
  plannedStart: businessDateSchema.nullable().optional(),
  plannedEnd: businessDateSchema.nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});

// ── Exclusão ────────────────────────────────────────────────────────────────────

export const deleteStructureNodeSchema = z.object({
  nodeType: z.enum(['TOWER', 'FLOOR', 'ACTIVITY', 'ITEM']),
  nodeId: idSchema,
  /** Confirmação explícita de que o conteúdo abaixo também será removido (RN-006). */
  cascade: z.boolean().default(false),
});
export type DeleteStructureNodeInput = z.infer<typeof deleteStructureNodeSchema>;

/**
 * O impacto perguntado antes de confirmar — mesma escolha de nó, sem a cascata.
 *
 * É consulta, não exclusão: a cascata só faz sentido em quem de fato remove.
 */
export const previewStructureNodeImpactSchema = z.object({
  nodeType: z.enum(['TOWER', 'FLOOR', 'ACTIVITY', 'ITEM']),
  nodeId: idSchema,
});
export type PreviewStructureNodeImpactInput = z.infer<typeof previewStructureNodeImpactSchema>;
